import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../trpc.js';
import {
  Alerta,
  Cartao,
  Carregando,
  ErroDeCarga,
  Tabela,
  Vazio,
  formatarMoeda,
  formatarNumero,
} from '../comum/ui.js';

/**
 * TELA DE CÁLCULO — rodar, comparar versões e ler a memória de cálculo.
 *
 * A memória é a razão de ser desta tela. Quando o cliente liga perguntando
 * "por que o líquido do fulano caiu R$ 80?", a resposta tem que sair daqui em
 * um minuto, com a conta escrita — não de uma planilha paralela.
 */
export function Calculo({ competencia }: { competencia: string }) {
  const [vinculoSelecionado, setVinculoSelecionado] = useState<string | null>(null);
  const [comparar, setComparar] = useState<{ a: number; b: number } | null>(null);

  const folha = useQuery({
    queryKey: ['folha-analitica', competencia],
    queryFn: () => api.relatorios.folhaAnalitica.query({ competencia }),
  });

  if (folha.isLoading) return <Carregando o="folha" />;
  if (folha.error) return <ErroDeCarga erro={folha.error} />;

  const dados = folha.data;
  if (!dados || dados.colaboradores.length === 0) {
    return <Vazio mensagem="Folha não calculada." sugestao="Aprove as entradas e rode o cálculo." />;
  }

  return (
    <div className="tela">
      <h1>Cálculo — {competencia}</h1>

      <Cartao
        titulo={`${dados.totais.quantidade} colaborador(es)`}
        acao={
          <button type="button" className="botao" onClick={() => setComparar({ a: 1, b: 2 })}>
            Comparar versões 1 × 2
          </button>
        }
      >
        <Tabela
          cabecalhos={['Matrícula', 'Nome', '#Proventos', '#Descontos', '#Líquido', '#Base INSS', '#FGTS', '']}
        >
          {dados.colaboradores.map((c) => (
            <tr key={c.matricula}>
              <td>{c.matricula}</td>
              <td>{c.nome}</td>
              <td className="numerica">{formatarMoeda(c.totalProventos)}</td>
              <td className="numerica">{formatarMoeda(c.totalDescontos)}</td>
              <td className="numerica destaque">{formatarMoeda(c.liquido)}</td>
              <td className="numerica">{formatarMoeda(c.bases.inss)}</td>
              <td className="numerica">{formatarMoeda(c.fgtsDepositado)}</td>
              <td>
                <button
                  type="button"
                  className="botao botao--texto"
                  onClick={() => setVinculoSelecionado(c.matricula)}
                >
                  Memória
                </button>
              </td>
            </tr>
          ))}
          <tr className="linha--total">
            <td colSpan={2}>TOTAL</td>
            <td className="numerica">{formatarMoeda(dados.totais.proventos)}</td>
            <td className="numerica">{formatarMoeda(dados.totais.descontos)}</td>
            <td className="numerica destaque">{formatarMoeda(dados.totais.liquido)}</td>
            <td colSpan={3} />
          </tr>
        </Tabela>
      </Cartao>

      {comparar ? <ComparacaoVersoes competencia={competencia} versoes={comparar} /> : null}

      {vinculoSelecionado ? (
        <MemoriaDeCalculo competencia={competencia} matricula={vinculoSelecionado} />
      ) : null}
    </div>
  );
}

function ComparacaoVersoes({
  competencia,
  versoes,
}: {
  competencia: string;
  versoes: { a: number; b: number };
}) {
  const consulta = useQuery({
    queryKey: ['comparar', competencia, versoes.a, versoes.b],
    queryFn: () =>
      api.calculo.compararVersoes.query({ competencia, versaoA: versoes.a, versaoB: versoes.b }),
  });

  if (consulta.isLoading) return <Carregando o="comparação" />;
  if (consulta.error) return <ErroDeCarga erro={consulta.error} />;

  const dados = consulta.data;
  if (!dados) return null;

  return (
    <Cartao titulo={`Versão ${versoes.a} × versão ${versoes.b}`}>
      <p>
        Total: {formatarMoeda(dados.totalAntes)} → {formatarMoeda(dados.totalDepois)}{' '}
        <strong className={dados.delta >= 0 ? 'positivo' : 'critico'}>
          ({dados.delta >= 0 ? '+' : ''}
          {formatarMoeda(dados.delta)})
        </strong>{' '}
        em {dados.vinculosAfetados} colaborador(es).
      </p>

      <Tabela cabecalhos={['Matrícula', 'Nome', '#Antes', '#Depois', '#Delta']}>
        {dados.diferencas.map((d) => (
          <tr key={d.matricula}>
            <td>{d.matricula}</td>
            <td>{d.nome}</td>
            <td className="numerica">{formatarMoeda(d.liquidoAntes)}</td>
            <td className="numerica">{formatarMoeda(d.liquidoDepois)}</td>
            <td className={`numerica ${d.delta >= 0 ? 'positivo' : 'critico'}`}>
              {d.delta >= 0 ? '+' : ''}
              {formatarMoeda(d.delta)}
            </td>
          </tr>
        ))}
      </Tabela>
    </Cartao>
  );
}

function MemoriaDeCalculo({ competencia, matricula }: { competencia: string; matricula: string }) {
  const consulta = useQuery({
    queryKey: ['memoria', competencia, matricula],
    // O roteador recebe vinculoId; a matrícula é o que o operador conhece.
    queryFn: () => api.calculo.memoria.query({ competencia, vinculoId: matricula }),
  });

  if (consulta.isLoading) return <Carregando o="memória de cálculo" />;
  if (consulta.error) return <ErroDeCarga erro={consulta.error} />;

  const dados = consulta.data;
  if (!dados) return null;

  return (
    <Cartao titulo={`Memória de cálculo — ${dados.colaborador.nome}`}>
      <Alerta severidade="INFO" titulo="Como ler">
        Cada linha mostra a base, a versão da tabela legal aplicada e a fórmula com os números
        substituídos. É o mesmo conteúdo que vai no relatório para a fiscalização.
      </Alerta>

      {dados.itens.map((item, i) => (
        <article key={`${item.rubrica}-${i}`} className="memoria">
          <header className="memoria__cabecalho">
            <strong>
              {item.rubrica} — {item.descricao}
            </strong>
            <span className={item.tipo === 'DESCONTO' ? 'critico' : 'positivo'}>
              {item.tipo === 'DESCONTO' ? '−' : '+'}
              {item.valorFormatado}
            </span>
          </header>

          <p className="memoria__formula">{item.memoria.formulaDescrita}</p>

          <dl className="memoria__meta">
            <dt>Base</dt>
            <dd>{formatarMoeda(item.memoria.base)}</dd>
            <dt>Tabela</dt>
            <dd>{item.memoria.refVersao}</dd>
            {item.memoria.faixaAplicada ? (
              <>
                <dt>Faixa</dt>
                <dd>{item.memoria.faixaAplicada}</dd>
              </>
            ) : null}
            <dt>Incidências</dt>
            <dd>
              CP {item.incidencias.cp} · IRRF {item.incidencias.irrf} · FGTS {item.incidencias.fgts}
            </dd>
          </dl>

          {item.memoria.parcelas.length > 0 ? (
            <Tabela cabecalhos={['Parcela', '#Base', '#Alíquota', '#Valor']}>
              {item.memoria.parcelas.map((p, j) => (
                <tr key={j}>
                  <td>{p.descricao}</td>
                  <td className="numerica">{formatarMoeda(p.base)}</td>
                  <td className="numerica">
                    {p.aliquota === null ? '—' : `${formatarNumero(p.aliquota)}%`}
                  </td>
                  <td className="numerica">{formatarMoeda(p.valor)}</td>
                </tr>
              ))}
            </Tabela>
          ) : null}
        </article>
      ))}
    </Cartao>
  );
}
